// frontend/src/pages/InventoryAuditPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
    ClipboardList, Search, Plus, Download, Upload, Smartphone, 
    CheckCircle, AlertTriangle, ArrowRight, Loader2, X, RefreshCw, Save, Package, Edit, Trash2, Unlock, Eye, Camera, Minus, ChevronLeft, ChevronRight, Check, FastForward
} from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';

const InventoryAuditPage = ({ apiFetch, formatCurrency, currentUser }) => {
    // Visões: 'list', 'create', 'manage', 'mobile', 'divergence'
    const [view, setView] = useState('list');
    
    // --- LÓGICA DE PERMISSÃO ---
    const hasFullAccess = currentUser?.is_super_user || currentUser?.permissions?.inventory_audit_create;
    const isOnlyCollector = !hasFullAccess && currentUser?.permissions?.inventory_mobile_collect;

    // --- ESTADOS GERAIS ---
    const [audits, setAudits] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // --- ESTADOS PARA LISTAGEM E EDIÇÃO ---
    const [searchQuery, setSearchQuery] = useState('');
    const [editForm, setEditForm] = useState(null);

    // --- ESTADOS PARA CRIAÇÃO ---
    const [deposits, setDeposits] = useState([]);
    const [classifications, setClassifications] = useState([]);
    const [createForm, setCreateForm] = useState({
        description: '', audit_type: 'general', deposit_id: '',
        filter_type: 'all', filter_value: '', 
        areas_config: [] 
    });

    const [createSearchQuery, setCreateSearchQuery] = useState('');
    const [createSearchResults, setCreateSearchResults] = useState([]);
    const [isSearchingCreate, setIsSearchingCreate] = useState(false);
    const [selectedCreateProducts, setSelectedCreateProducts] = useState([]); 

    // --- ESTADOS PARA O INVENTÁRIO ATIVO (MANAGE) ---
    const [activeAudit, setActiveAudit] = useState(null);

    // --- ESTADOS PARA VISUALIZAR SESSÃO ESPECÍFICA NO MANAGE ---
    const [viewingSession, setViewingSession] = useState(null);
    const [viewingSessionHistory, setViewingSessionHistory] = useState([]);
    const [isLoadingSession, setIsLoadingSession] = useState(false);
    
    // Estados de pesquisa e paginação para o modal
    const [sessionSearchTerm, setSessionSearchTerm] = useState('');
    const [sessionPage, setSessionPage] = useState(1);

    // --- ESTADOS PARA MOBILE COLLECT ---
    const [mobileDeviceNumber, setMobileDeviceNumber] = useState(null);
    const [mobileSessionId, setMobileSessionId] = useState(null);
    const [mobileSessionStatus, setMobileSessionStatus] = useState('active');
    
    const [mobileSearchQuery, setMobileSearchQuery] = useState('');
    const [mobileSearchResults, setMobileSearchResults] = useState([]);
    const [isSearchingMobile, setIsSearchingMobile] = useState(false);
    const [mobileSelectedProduct, setMobileSelectedProduct] = useState(null);
    
    // Seletores fixos no topo do Mobile
    const [mobileDepositId, setMobileDepositId] = useState('');
    const [mobileAreaNumber, setMobileAreaNumber] = useState('');
    
    const [mobileQuantity, setMobileQuantity] = useState('');
    const [mobileAction, setMobileAction] = useState('sum');
    const [mobileHistory, setMobileHistory] = useState([]);
    
    // --- ESTADO PARA CÂMERA ---
    const [isScanning, setIsScanning] = useState(false);

    // --- ESTADOS PARA DIVERGÊNCIAS ---
    const [divergences, setDivergences] = useState([]);
    const [isApplying, setIsApplying] = useState(false);

    const fetchAudits = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await apiFetch('/inventory');
            setAudits(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [apiFetch]);

    const fetchSupportingData = useCallback(async () => {
        try {
            const deps = await apiFetch('/deposits');
            const activeDeps = deps.filter(d => d.status === 'ativo');
            setDeposits(activeDeps);
            
            // Inicializa a configuração de áreas
            const initialConfig = activeDeps.map(d => ({
                deposit_id: d.id,
                deposit_name: d.name,
                areas: [{ area: 1, expected: 1, is_empty: false, is_done: false }]
            }));
            setCreateForm(prev => ({ ...prev, areas_config: initialConfig }));
            
            const classifs = await apiFetch('/product-classifications');
            setClassifications(classifs);
        } catch (err) { console.error(err); }
    }, [apiFetch]);

    useEffect(() => {
        if (view === 'list') fetchAudits();
        if (view === 'create' && hasFullAccess) fetchSupportingData();
    }, [view, fetchAudits, fetchSupportingData, hasFullAccess]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            if (createSearchQuery.length >= 2 && view === 'create') {
                setIsSearchingCreate(true);
                try {
                    const results = await apiFetch(`/stock/search?q=${encodeURIComponent(createSearchQuery)}`);
                    setCreateSearchResults(results);
                } catch (err) { console.error(err); } finally { setIsSearchingCreate(false); }
            } else {
                setCreateSearchResults([]);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [createSearchQuery, view, apiFetch]);

    const handleSelectCreateProduct = (prod) => {
        if (!selectedCreateProducts.find(p => p.id === prod.id)) {
            const newSelection = [...selectedCreateProducts, prod];
            setSelectedCreateProducts(newSelection);
            setCreateForm({ ...createForm, filter_value: JSON.stringify(newSelection.map(p => p.id)) });
        }
        setCreateSearchQuery('');
        setCreateSearchResults([]);
    };

    const handleRemoveCreateProduct = (prodId) => {
        const newSelection = selectedCreateProducts.filter(p => p.id !== prodId);
        setSelectedCreateProducts(newSelection);
        setCreateForm({ ...createForm, filter_value: JSON.stringify(newSelection.map(p => p.id)) });
    };

    // --- CONTROLES DE ÁREA NO FORMULÁRIO DE CRIAÇÃO ---
    const handleDepositAreaCountChange = (depositId, countStr) => {
        const count = parseInt(countStr) || 1;
        const newConfig = [...createForm.areas_config];
        const depIndex = newConfig.findIndex(d => d.deposit_id === depositId);
        
        if (depIndex !== -1) {
            const currentAreas = newConfig[depIndex].areas;
            newConfig[depIndex].areas = Array.from({ length: count }, (_, i) => ({
                area: i + 1,
                expected: currentAreas[i]?.expected || 1,
                is_empty: currentAreas[i]?.is_empty || false,
                is_done: false
            }));
            setCreateForm({ ...createForm, areas_config: newConfig });
        }
    };

    const handleAreaExpectedChange = (depositId, areaIndex, valStr) => {
        const val = parseInt(valStr) || 1;
        const newConfig = [...createForm.areas_config];
        const depIndex = newConfig.findIndex(d => d.deposit_id === depositId);
        if (depIndex !== -1) {
            newConfig[depIndex].areas[areaIndex].expected = val;
            setCreateForm({ ...createForm, areas_config: newConfig });
        }
    };

    const handleCreateAudit = async (e) => {
        e.preventDefault();
        setError('');

        if (createForm.filter_type === 'product' && selectedCreateProducts.length === 0) {
            setError('Por favor, busque e adicione pelo menos um produto ao inventário.');
            return;
        }

        let finalConfig = createForm.areas_config;
        if (createForm.audit_type === 'deposit') {
            finalConfig = createForm.areas_config.filter(d => d.deposit_id === parseInt(createForm.deposit_id));
        }

        setIsLoading(true);
        try {
            const res = await apiFetch('/inventory', {
                method: 'POST',
                body: { ...createForm, areas_config: finalConfig }
            });
            setSuccess(`Inventário ${res.audit_number} criado!`);
            
            setSelectedCreateProducts([]);
            setCreateSearchQuery('');
            setView('list');
            fetchAudits();
            openManage(res.id);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await apiFetch(`/inventory/${editForm.id}`, {
                method: 'PUT',
                body: { description: editForm.description }
            });
            setSuccess('Inventário atualizado!');
            setEditForm(null);
            fetchAudits();
        } catch (err) {
            alert(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteAudit = async (id, number) => {
        if(!window.confirm(`Tem a certeza que deseja excluir permanentemente o inventário ${number}?`)) return;
        try {
            await apiFetch(`/inventory/${id}`, { method: 'DELETE' });
            setSuccess('Inventário excluído!');
            fetchAudits();
        } catch (err) {
            alert(err.message);
        }
    };

    const openManage = async (id) => {
        setIsLoading(true);
        setError('');
        setSuccess('');
        try {
            const data = await apiFetch(`/inventory/${id}`);
            setActiveAudit(data);

            if (isOnlyCollector) {
                if (data.status === 'in_progress') {
                    initMobileCollector(data);
                } else {
                    setError('Este inventário já foi finalizado ou está em análise. Você não pode mais coletar itens.');
                    setView('list');
                }
                setIsLoading(false);
                return;
            }

            if (data.status === 'divergence' || data.status === 'completed') {
                await loadDivergences(id);
                setView('divergence');
            } else {
                setView('manage');
            }
        } catch (err) {
            setError('Erro ao abrir inventário.');
            setView('list');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRefreshStatus = () => openManage(activeAudit.id);

    // --- ATUALIZAR ÁREAS DINAMICAMENTE E MARCAR STATUS (TELA ADMIN) ---
    const updateAreaExpected = async (depositId, areaNum, increment) => {
        if (!activeAudit) return;
        
        const currentConfig = JSON.parse(JSON.stringify(activeAudit.areas_config)) || [];
        const depIndex = currentConfig.findIndex(d => d.deposit_id === depositId);
        if (depIndex === -1) return;

        const targetArea = currentConfig[depIndex].areas.find(a => a.area === areaNum);
        if (!targetArea) return;

        if (increment < 0) {
            const currentSessionsInArea = activeAudit.sessions?.filter(s => s.current_deposit_id === depositId && s.area_number === areaNum).length || 0;
            if (targetArea.expected <= currentSessionsInArea) {
                alert('Não é possível reduzir. Esta área já possui esta quantidade de coletores logados.');
                return;
            }
        }

        targetArea.expected = Math.max(1, targetArea.expected + increment);

        try {
            setIsApplying(true);
            await apiFetch(`/inventory/${activeAudit.id}/areas`, {
                method: 'PUT',
                body: { areas_config: currentConfig }
            });
            await openManage(activeAudit.id);
        } catch (err) {
            alert('Erro ao atualizar área: ' + err.message);
        } finally {
            setIsApplying(false);
        }
    };

    const markAreaAsEmpty = async (depositId, areaNum) => {
        if (!window.confirm(`Marcar a Área ${areaNum} como VAZIA? O sistema considerará esta área concluída com zero produtos.`)) return;
        
        const currentConfig = JSON.parse(JSON.stringify(activeAudit.areas_config)) || [];
        const depIndex = currentConfig.findIndex(d => d.deposit_id === depositId);
        if (depIndex === -1) return;

        const targetArea = currentConfig[depIndex].areas.find(a => a.area === areaNum);
        if (!targetArea) return;

        targetArea.is_empty = true;

        try {
            setIsApplying(true);
            await apiFetch(`/inventory/${activeAudit.id}/areas`, {
                method: 'PUT',
                body: { areas_config: currentConfig }
            });
            await openManage(activeAudit.id);
        } catch (err) {
            alert('Erro ao atualizar área: ' + err.message);
        } finally {
            setIsApplying(false);
        }
    };

    const markAreaAsDone = async (depositId, areaNum) => {
        if (!window.confirm(`Forçar conclusão da Área ${areaNum}? O sistema fechará a coleta para esta área, ignorando coletores pendentes.`)) return;
        
        const currentConfig = JSON.parse(JSON.stringify(activeAudit.areas_config)) || [];
        const depIndex = currentConfig.findIndex(d => d.deposit_id === depositId);
        if (depIndex === -1) return;

        const targetArea = currentConfig[depIndex].areas.find(a => a.area === areaNum);
        if (!targetArea) return;

        targetArea.is_done = true;

        try {
            setIsApplying(true);
            await apiFetch(`/inventory/${activeAudit.id}/areas`, {
                method: 'PUT',
                body: { areas_config: currentConfig }
            });
            await openManage(activeAudit.id);
        } catch (err) {
            alert('Erro ao forçar conclusão da área: ' + err.message);
        } finally {
            setIsApplying(false);
        }
    };

    const forceGlobalConclusion = async () => {
        if (!window.confirm('Tem certeza que deseja FORÇAR A CONCLUSÃO DO INVENTÁRIO INTEIRO agora? Isso fechará todas as áreas pendentes e gerará as divergências imediatamente.')) return;
        
        setIsApplying(true);
        try {
            const res = await apiFetch(`/inventory/${activeAudit.id}/force-divergence`, { method: 'POST' });
            setSuccess(res.message);
            await openManage(activeAudit.id);
        } catch (err) {
            alert('Erro ao forçar conclusão: ' + err.message);
        } finally {
            setIsApplying(false);
        }
    };

    const handleViewSessionHistory = async (session) => {
        setViewingSession(session);
        setSessionSearchTerm('');
        setSessionPage(1);
        setIsLoadingSession(true);
        try {
            const data = await apiFetch(`/inventory/${activeAudit.id}/mobile/session/${session.id}`);
            setViewingSessionHistory(data);
        } catch (err) {
            alert('Erro ao carregar coletas deste aparelho.');
        } finally {
            setIsLoadingSession(false);
        }
    };

    const exportToCSV = async () => {
        try {
            const data = await apiFetch(`/inventory/${activeAudit.id}/export-data`);
            if (data.length === 0) return alert('Nenhum produto encontrado para os filtros deste inventário.');

            let csvContent = "ID_Variacao;ID_Deposito;Codigo;EAN;Produto;Deposito;Quantidade_Coletada\n";
            data.forEach(row => {
                csvContent += `${row.variation_id};${row.deposit_id};${row.codigo || ''};${row.ean || ''};"${row.produto}";"${row.deposito}";\n`;
            });

            const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Inventario_${activeAudit.audit_number}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            alert('Erro ao exportar: ' + err.message);
        }
    };

    const exportDivergencesToCSV = () => {
        if (divergences.length === 0) return alert('Nenhuma divergência para exportar.');

        let csvContent = "Produto;Deposito;Saldo Sistema;Coletado;Furo (Qtd);Custo Unitario;Diferenca (RS)\n";
        
        divergences.forEach(item => {
            const prodName = item.product_name ? item.product_name.replace(/"/g, '""') : '';
            const depName = item.deposit_name ? item.deposit_name.replace(/"/g, '""') : '';
            
            const sysQty = item.system_quantity.toString().replace('.', ',');
            const colQty = item.collected_quantity.toString().replace('.', ',');
            const diffQtyStr = item.diff_quantity.toString().replace('.', ',');
            
            const unitCostStr = (item.cost_price || 0).toFixed(2).replace('.', ',');
            const diffCostStr = (item.diff_total_cost || 0).toFixed(2).replace('.', ',');

            csvContent += `"${prodName}";"${depName}";${sysQty};${colQty};${diffQtyStr};${unitCostStr};${diffCostStr}\n`;
        });

        const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Divergencias_${activeAudit?.audit_number}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleImportCSV = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const text = event.target.result;
                const lines = text.split('\n');
                const items = [];

                for (let i = 1; i < lines.length; i++) {
                    if (!lines[i].trim()) continue;
                    
                    const columns = lines[i].split(/;(?=(?:(?:[^"]*"){2})*[^"]*$)/); 
                    if (columns.length >= 7) {
                        const varId = parseInt(columns[0]);
                        const depId = parseInt(columns[1]);
                        const qtyStr = columns[6].replace(/"/g, '').replace(',', '.').trim();
                        const qty = qtyStr ? parseFloat(qtyStr) : 0;

                        if (!isNaN(varId) && !isNaN(depId)) {
                            items.push({ variation_id: varId, deposit_id: depId, collected_quantity: qty });
                        }
                    }
                }

                setIsLoading(true);
                await apiFetch(`/inventory/${activeAudit.id}/import`, {
                    method: 'POST',
                    body: { items }
                });
                setSuccess('Planilha importada com sucesso!');
                openManage(activeAudit.id);
            } catch (err) {
                alert('Erro ao processar arquivo: ' + err.message);
            } finally {
                setIsLoading(false);
                e.target.value = null; 
            }
        };
        reader.readAsText(file);
    };

    // --- LÓGICA DE START DO MOBILE (APENAS PEDE O COLETOR) ---
    const initMobileCollector = async (auditData = activeAudit) => {
        const deviceInput = window.prompt(`Informe o seu NÚMERO DE COLETOR para esta sessão:`);
        if (!deviceInput) return;
        const deviceNum = parseInt(deviceInput, 10);
        
        if (isNaN(deviceNum) || deviceNum < 1) {
            alert(`Número de coletor inválido.`);
            if(isOnlyCollector) setView('list');
            return;
        }

        setMobileDeviceNumber(deviceNum);
        setMobileSessionId(null);
        setMobileHistory([]);
        setMobileSelectedProduct(null);
        setMobileSessionStatus('active');
        setSuccess('');

        // Tenta achar o primeiro depósito e área disponíveis para pré-selecionar
        const config = auditData.areas_config || [];
        const availableDeposits = config.filter(c => c.areas.length > 0 && !c.areas.every(a => a.is_empty || a.is_done));
        
        let initialDep = '';
        let initialArea = '';

        if (availableDeposits.length > 0) {
            initialDep = availableDeposits[0].deposit_id;
            const activeAreas = availableDeposits[0].areas.filter(a => !a.is_empty && !a.is_done);
            if(activeAreas.length > 0) initialArea = activeAreas[0].area;
        } else if (config.length > 0) {
            initialDep = config[0].deposit_id;
            if(config[0].areas.length > 0) initialArea = config[0].areas[0].area;
        }

        setMobileDepositId(initialDep);
        setMobileAreaNumber(initialArea);

        if (deposits.length === 0) fetchSupportingData(); 

        setView('mobile');
    };

    // Efeito para carregar a sessão do servidor quando Depósito ou Área mudarem
    useEffect(() => {
        const loadSessionForArea = async () => {
            if (view === 'mobile' && activeAudit && mobileDepositId && mobileAreaNumber && mobileDeviceNumber) {
                setIsLoading(true);
                try {
                    const res = await apiFetch(`/inventory/${activeAudit.id}/mobile/start`, { 
                        method: 'POST',
                        body: { 
                            device_number: mobileDeviceNumber,
                            deposit_id: parseInt(mobileDepositId),
                            area_number: parseInt(mobileAreaNumber)
                        }
                    });
                    
                    setMobileSessionId(res.session_id);
                    setMobileSessionStatus(res.session_status || 'active');
                    setMobileHistory(res.history || []); 
                    setMobileSelectedProduct(null);
                    
                    if (res.session_status === 'finished') {
                        setSuccess('Esta área já foi finalizada. Modo de leitura.');
                    } else {
                        setSuccess('');
                    }
                } catch (err) {
                    setError(err.message);
                } finally {
                    setIsLoading(false);
                }
            }
        };
        
        loadSessionForArea();
    }, [view, activeAudit, mobileDepositId, mobileAreaNumber, mobileDeviceNumber, apiFetch]);

    // --- CONTROLE DA CÂMERA (LEITOR DE CÓDIGO DE BARRAS) ---
    useEffect(() => {
        let html5QrCode;
        if (isScanning) {
            const timer = setTimeout(() => {
                html5QrCode = new Html5Qrcode("reader");
                html5QrCode.start(
                    { facingMode: "environment" },
                    { fps: 10, qrbox: { width: 250, height: 150 } },
                    (decodedText) => {
                        setMobileSearchQuery(decodedText);
                        setIsScanning(false);
                    },
                    (errorMessage) => { }
                ).catch(err => {
                    console.error("Camera Error:", err);
                    alert("Acesso bloqueado. O navegador exige que o sistema esteja em HTTPS para ligar a câmera.");
                    setIsScanning(false);
                });
            }, 100);

            return () => {
                clearTimeout(timer);
                if (html5QrCode && html5QrCode.isScanning) {
                    html5QrCode.stop().then(() => html5QrCode.clear()).catch(console.error);
                }
            };
        }
    }, [isScanning]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            if (mobileSearchQuery.length >= 2 && view === 'mobile') {
                setIsSearchingMobile(true);
                try {
                    const results = await apiFetch(`/stock/search?q=${encodeURIComponent(mobileSearchQuery)}`);
                    setMobileSearchResults(results);
                } catch (err) { console.error(err); } finally { setIsSearchingMobile(false); }
            } else {
                setMobileSearchResults([]);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [mobileSearchQuery, view, apiFetch]);

    const selectMobileProduct = (prod) => {
        if (!mobileDepositId || !mobileAreaNumber) {
            return alert('Por favor, selecione o Depósito e a Área no topo da tela antes de bipar um produto.');
        }
        setMobileSelectedProduct(prod);
        setMobileSearchQuery('');
        setMobileSearchResults([]);
    };

    const handleMobileCollect = async (e) => {
        e.preventDefault();
        if (!mobileSelectedProduct || !mobileDepositId || !mobileAreaNumber || !mobileQuantity) return;
        
        if (mobileSessionStatus === 'finished') {
            return alert('Sessão finalizada. Você não pode mais coletar nesta área.');
        }

        const qty = parseFloat(mobileQuantity.replace(',', '.'));
        if (isNaN(qty) || qty < 0) return alert('Quantidade inválida.');

        try {
            await apiFetch(`/inventory/${activeAudit.id}/mobile/collect`, {
                method: 'POST',
                body: {
                    session_id: mobileSessionId,
                    variation_id: mobileSelectedProduct.id,
                    deposit_id: parseInt(mobileDepositId),
                    area_number: parseInt(mobileAreaNumber),
                    quantity: qty,
                    action: mobileAction
                }
            });

            setMobileHistory(prev => [{
                id: Date.now(),
                name: mobileSelectedProduct.name,
                qty: qty,
                action: mobileAction,
                time: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
            }, ...prev]);

            setMobileSelectedProduct(null);
            setMobileQuantity('');
            setMobileAction('sum');
            setSuccess('Item coletado!');
            setTimeout(() => setSuccess(''), 2000);
        } catch (err) {
            alert(err.message); 
        }
    };

    const finishMobileSession = async () => {
        if(!window.confirm('Tem certeza que deseja finalizar a contagem desta ÁREA neste DEPÓSITO?')) return;
        setIsLoading(true);
        try {
            const res = await apiFetch(`/inventory/${activeAudit.id}/mobile/finish`, { 
                method: 'POST',
                body: { session_id: mobileSessionId }
            });
            
            alert(res.message);
            setMobileSessionStatus('finished');
            
            if (res.status === 'divergence') {
                if (isOnlyCollector) setView('list');
                else openManage(activeAudit.id);
            } else {
                setSuccess('A área foi finalizada. Selecione outra área no topo para continuar.');
            }
        } catch (err) {
            alert(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const loadDivergences = async (id) => {
        const data = await apiFetch(`/inventory/${id}/divergences`);
        setDivergences(data);
    };

    const updateDivergenceItem = async (itemId, newQty) => {
        const parsedQty = parseFloat(newQty);
        if (isNaN(parsedQty)) return;

        setDivergences(prev => prev.map(d => {
            if (d.id === itemId) {
                const diff = parsedQty - d.system_quantity;
                return { ...d, collected_quantity: parsedQty, diff_quantity: diff, diff_total_cost: diff * d.cost_price };
            }
            return d;
        }));

        try {
            await apiFetch(`/inventory/${activeAudit.id}/items/${itemId}`, {
                method: 'PUT',
                body: { collected_quantity: parsedQty }
            });
        } catch (err) {
            alert('Falha ao salvar correção no servidor.');
            loadDivergences(activeAudit.id); 
        }
    };

    const handleReopenCollect = async () => {
        if (!window.confirm('Atenção: Isso descartará a análise de divergências atual e reabrirá o inventário para coleta móvel. Deseja continuar?')) return;
        setIsApplying(true);
        try {
            const res = await apiFetch(`/inventory/${activeAudit.id}/reopen`, { method: 'POST' });
            setSuccess(res.message || 'Coleta reaberta com sucesso.');
            openManage(activeAudit.id);
        } catch (err) {
            alert(err.message);
        } finally {
            setIsApplying(false);
        }
    };

    const applyInventory = async () => {
        if (!window.confirm('ATENÇÃO: Isso irá SOBRESCREVER os saldos atuais do sistema com as contagens coletadas. Deseja prosseguir?')) return;
        setIsApplying(true);
        try {
            const res = await apiFetch(`/inventory/${activeAudit.id}/apply`, { method: 'POST' });
            setSuccess(res.message);
            openManage(activeAudit.id); 
        } catch (err) {
            alert(err.message);
        } finally {
            setIsApplying(false);
        }
    };

    const renderList = () => {
        const filtered = audits.filter(a => 
            a.audit_number.toLowerCase().includes(searchQuery.toLowerCase()) || 
            a.description.toLowerCase().includes(searchQuery.toLowerCase())
        );

        return (
            <div className="animate-in fade-in">
                <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-3 text-gray-400" size={18} />
                        <input 
                            type="text" placeholder="Buscar por número ou descrição..." 
                            value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                            className="w-full pl-10 p-2.5 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                        />
                    </div>
                    {hasFullAccess && (
                        <button onClick={() => setView('create')} className="bg-indigo-600 text-white px-4 py-2.5 rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-indigo-700">
                            <Plus size={20}/> Novo Inventário
                        </button>
                    )}
                </div>

                {success && <div className="p-3 bg-green-100 text-green-700 rounded-md mb-4 font-bold text-center">{success}</div>}
                {error && <div className="p-3 bg-red-100 text-red-700 rounded-md mb-4 font-bold text-center">{error}</div>}

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50 border-b text-gray-500 text-xs uppercase tracking-wider">
                                <th className="p-4 font-bold">Número / Data</th>
                                <th className="p-4 font-bold hidden md:table-cell">Descrição</th>
                                <th className="p-4 font-bold">Status</th>
                                <th className="p-4 font-bold text-center">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filtered.length === 0 ? (
                                <tr><td colSpan="4" className="p-8 text-center text-gray-500">Nenhum inventário encontrado.</td></tr>
                            ) : filtered.map(audit => (
                                <tr key={audit.id} className="hover:bg-indigo-50/50 transition-colors">
                                    <td className="p-4">
                                        <p className="font-bold text-indigo-700">{audit.audit_number}</p>
                                        <p className="text-xs text-gray-500">{new Date(audit.created_at).toLocaleDateString('pt-BR')}</p>
                                    </td>
                                    <td className="p-4 hidden md:table-cell text-sm text-gray-700">{audit.description}</td>
                                    <td className="p-4">
                                        {audit.status === 'in_progress' && <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-md">Em Coleta</span>}
                                        {audit.status === 'divergence' && <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-bold rounded-md">Divergências</span>}
                                        {audit.status === 'completed' && <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-bold rounded-md">Concluído</span>}
                                    </td>
                                    <td className="p-4 text-center">
                                        <div className="flex items-center justify-center gap-3">
                                            <button onClick={() => openManage(audit.id)} className="text-indigo-600 hover:text-indigo-900 font-semibold text-sm">
                                                Acessar
                                            </button>
                                            {hasFullAccess && audit.status === 'in_progress' && (
                                                <>
                                                    <span className="text-gray-300">|</span>
                                                    <button onClick={() => setEditForm({ id: audit.id, description: audit.description })} className="text-gray-400 hover:text-blue-600" title="Editar Inventário">
                                                        <Edit size={16} />
                                                    </button>
                                                    <button onClick={() => handleDeleteAudit(audit.id, audit.audit_number)} className="text-gray-400 hover:text-red-600" title="Excluir Inventário">
                                                        <Trash2 size={16} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {editForm && (
                    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black bg-opacity-50 p-4 animate-in fade-in">
                        <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                            <div className="flex justify-between items-center mb-4 border-b pb-2">
                                <h3 className="font-bold text-lg text-gray-800">Editar Inventário</h3>
                                <button onClick={() => setEditForm(null)} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                            </div>
                            <form onSubmit={handleEditSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Descrição</label>
                                    <input type="text" required value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
                                </div>
                                <div className="pt-2">
                                    <button type="submit" disabled={isLoading} className="w-full bg-indigo-600 text-white font-bold py-3 rounded-lg hover:bg-indigo-700 flex justify-center items-center gap-2">
                                        {isLoading ? <Loader2 size={18} className="animate-spin"/> : 'Salvar Alterações'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderCreate = () => {
        const activeDeps = createForm.audit_type === 'deposit' && createForm.deposit_id 
            ? deposits.filter(d => d.id === parseInt(createForm.deposit_id)) 
            : deposits;

        return (
            <div className="max-w-4xl mx-auto bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-200 animate-in slide-in-from-right-8">
                <div className="flex items-center gap-3 mb-6 border-b pb-4">
                    <button onClick={() => setView('list')} className="p-2 bg-gray-100 rounded-full hover:bg-gray-200"><ArrowRight size={20} className="rotate-180"/></button>
                    <h2 className="text-2xl font-bold text-gray-800">Criar Novo Inventário</h2>
                </div>
                
                {error && <div className="p-3 bg-red-100 text-red-700 rounded-md mb-4">{error}</div>}

                <form onSubmit={handleCreateAudit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Descrição / Motivo</label>
                        <input type="text" required placeholder="Ex: Inventário Anual 2026, Contagem Loja Matriz..." 
                            value={createForm.description} onChange={e => setCreateForm({...createForm, description: e.target.value})}
                            className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Escopo</label>
                            <select value={createForm.audit_type} onChange={e => setCreateForm({...createForm, audit_type: e.target.value, deposit_id: ''})} className="w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-indigo-500">
                                <option value="general">Geral (Todos os Depósitos)</option>
                                <option value="deposit">Por Depósito Específico</option>
                            </select>
                        </div>
                        {createForm.audit_type === 'deposit' && (
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Selecione o Depósito</label>
                                <select required value={createForm.deposit_id} onChange={e => setCreateForm({...createForm, deposit_id: e.target.value})} className="w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-indigo-500">
                                    <option value="">Selecione...</option>
                                    {deposits.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg border border-gray-100">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Filtro de Produtos</label>
                            <select value={createForm.filter_type} onChange={e => {
                                setCreateForm({...createForm, filter_type: e.target.value, filter_value: ''});
                                setSelectedCreateProducts([]);
                                setCreateSearchQuery('');
                            }} className="w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-indigo-500">
                                <option value="all">Todos os Produtos</option>
                                <option value="classification">Por Classificação (Grupo)</option>
                                <option value="product">Produto(s) Específico(s)</option>
                            </select>
                        </div>
                        
                        {createForm.filter_type === 'classification' && (
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Selecione a Classificação</label>
                                <select required value={createForm.filter_value} onChange={e => setCreateForm({...createForm, filter_value: e.target.value})} className="w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-indigo-500">
                                    <option value="">Selecione...</option>
                                    {classifications.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                        )}

                        {createForm.filter_type === 'product' && (
                            <div className="relative md:col-span-2">
                                <label className="block text-sm font-bold text-gray-700 mb-1">Buscar Produtos Específicos</label>
                                
                                <div className="relative mb-2">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Search size={16} className="text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Digite o nome, código ou EAN para adicionar..."
                                        value={createSearchQuery}
                                        onChange={(e) => setCreateSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                                    />
                                    {isSearchingCreate && (
                                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                            <Loader2 size={16} className="animate-spin text-indigo-500" />
                                        </div>
                                    )}
                                    {createSearchResults.length > 0 && (
                                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 shadow-xl rounded-lg max-h-48 overflow-y-auto">
                                            {createSearchResults.map(prod => (
                                                <div key={prod.id} onClick={() => handleSelectCreateProduct(prod)} className="p-2 border-b border-gray-100 hover:bg-indigo-50 cursor-pointer">
                                                    <p className="font-bold text-sm text-gray-800 leading-tight">{prod.name}</p>
                                                    <p className="text-xs text-gray-500">Cód: {prod.code || '-'} | EAN: {prod.ean || '-'}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {selectedCreateProducts.length > 0 && (
                                    <div className="space-y-2 mt-3">
                                        <p className="text-xs font-bold text-indigo-700 uppercase">Produtos Selecionados ({selectedCreateProducts.length}):</p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {selectedCreateProducts.map(prod => (
                                                <div key={prod.id} className="flex items-center justify-between p-2 border border-indigo-200 bg-indigo-50 rounded-lg">
                                                    <div className="flex items-center gap-2 truncate">
                                                        <Package size={16} className="text-indigo-600 flex-shrink-0"/>
                                                        <span className="text-xs font-bold text-indigo-900 truncate">{prod.name}</span>
                                                    </div>
                                                    <button type="button" onClick={() => handleRemoveCreateProduct(prod.id)} className="text-indigo-400 hover:text-red-500 flex-shrink-0 ml-2">
                                                        <X size={16}/>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* --- GERENCIAMENTO DE ÁREAS POR DEPÓSITO NA CRIAÇÃO --- */}
                    <div className="border-t pt-4">
                        <h3 className="font-bold text-gray-800 mb-1">Mapeamento de Áreas por Depósito</h3>
                        <p className="text-xs text-gray-500 mb-4">Divida os depósitos em áreas/corredores. Para não contar um depósito, marque 0 áreas.</p>
                        
                        <div className="space-y-6">
                            {activeDeps.map(dep => {
                                const depConfig = createForm.areas_config.find(c => c.deposit_id === dep.id) || { areas: [] };
                                
                                return (
                                    <div key={dep.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                        <div className="flex justify-between items-center mb-3 border-b border-gray-200 pb-2">
                                            <h4 className="font-bold text-indigo-900">{dep.name}</h4>
                                            <div className="flex items-center gap-2">
                                                <label className="text-xs font-bold text-gray-500">Total de Áreas:</label>
                                                <input 
                                                    type="number" min="0" max="50" 
                                                    value={depConfig.areas.length} 
                                                    onChange={e => handleDepositAreaCountChange(dep.id, e.target.value)} 
                                                    className="w-16 p-1 text-center border border-gray-300 rounded outline-none font-bold" 
                                                />
                                            </div>
                                        </div>
                                        
                                        {depConfig.areas.length > 0 ? (
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-h-48 overflow-y-auto pr-1">
                                                {depConfig.areas.map((area, index) => (
                                                    <div key={index} className="bg-white p-2 rounded border border-gray-200 flex flex-col items-center">
                                                        <span className="text-[10px] font-bold text-gray-400 mb-1">ÁREA {area.area}</span>
                                                        <label className="text-[10px] font-semibold text-gray-700 text-center">Coletores Esp.</label>
                                                        <input type="number" min="1" required value={area.expected} onChange={e => handleAreaExpectedChange(dep.id, index, e.target.value)} className="w-full mt-1 p-1 text-center border rounded bg-gray-50 focus:bg-white outline-none text-xs" />
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-gray-400 italic">Depósito ignorado (Nenhuma área configurada).</p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex justify-end pt-4">
                        <button type="submit" disabled={isLoading} className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-indigo-700 disabled:opacity-50">
                            {isLoading ? 'Criando...' : 'Iniciar Inventário'}
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    const renderManage = () => {
        if (!activeAudit) return null;

        const currentConfig = activeAudit.areas_config || [];
        let totalExpected = 0;
        let totalFinished = 0;

        currentConfig.forEach(dep => {
            dep.areas.forEach(a => {
                if (!a.is_empty) {
                    if (a.is_done) {
                        totalExpected += a.expected;
                        totalFinished += a.expected;
                    } else {
                        totalExpected += a.expected;
                        totalFinished += activeAudit.sessions?.filter(s => s.current_deposit_id === dep.deposit_id && s.area_number === a.area && s.status === 'finished').length || 0;
                    }
                }
            });
        });

        const progressPercent = totalExpected > 0 ? Math.min(100, Math.round((totalFinished / totalExpected) * 100)) : 0;

        return (
            <div className="max-w-5xl mx-auto space-y-6 animate-in zoom-in-95 duration-300">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <button onClick={() => setView('list')} className="text-indigo-600 text-sm font-semibold hover:underline mb-2 flex items-center gap-1"><ArrowRight size={14} className="rotate-180"/> Voltar para Lista</button>
                            <h2 className="text-2xl font-black text-gray-800">{activeAudit.audit_number}</h2>
                            <p className="text-gray-600">{activeAudit.description}</p>
                        </div>
                        <div className="text-right">
                            <span className="inline-block px-3 py-1 bg-yellow-100 text-yellow-800 font-bold rounded-full text-sm">FASE 1: COLETA EM ANDAMENTO</span>
                        </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm bg-gray-50 p-3 rounded-lg border mb-4">
                        <span className="font-semibold text-gray-700">Tipo: <span className="font-normal">{activeAudit.audit_type === 'general' ? 'Geral (Todos)' : 'Por Depósito'}</span></span>
                        <span className="font-semibold text-gray-700">Progresso Global: <span className={`font-bold ${progressPercent === 100 ? 'text-green-600' : 'text-blue-600'}`}>{progressPercent}%</span></span>
                    </div>

                    {/* BARRA DE PROGRESSO VISUAL */}
                    <div className="w-full bg-gray-200 rounded-full h-3 mb-1 overflow-hidden">
                        <div className={`h-3 rounded-full transition-all duration-500 ${progressPercent === 100 ? 'bg-green-500' : 'bg-blue-600'}`} style={{ width: `${progressPercent}%` }}></div>
                    </div>
                    <p className="text-[10px] text-right text-gray-500 font-bold uppercase">{totalFinished} de {totalExpected} coletores finalizaram</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* OPÇÃO 1: EXCEL */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-blue-100 flex flex-col justify-between">
                        <div>
                            <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-4"><ClipboardList size={24}/></div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">Opção 1: Via Planilha</h3>
                            <p className="text-sm text-gray-500 mb-6">Baixe a planilha base, preencha as quantidades e faça o upload para cruzar os dados.</p>
                        </div>
                        <div className="space-y-3">
                            <button onClick={exportToCSV} className="w-full flex justify-center items-center gap-2 bg-white border-2 border-blue-600 text-blue-600 font-bold py-2.5 rounded-lg hover:bg-blue-50 transition-colors">
                                <Download size={18}/> 1. Exportar Planilha Base
                            </button>
                            <div className="relative w-full">
                                <input type="file" accept=".csv" onChange={handleImportCSV} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                                <button className="w-full flex justify-center items-center gap-2 bg-blue-600 text-white font-bold py-3 rounded-lg hover:bg-blue-700 transition-colors">
                                    <Upload size={18}/> 2. Importar Contagem (CSV)
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* OPÇÃO 2: MOBILE */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-indigo-100 flex flex-col justify-between">
                        <div>
                            <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-lg flex items-center justify-center mb-4"><Smartphone size={24}/></div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">Opção 2: Via Celular</h3>
                            <p className="text-sm text-gray-500 mb-6">Colete bipando ou digitando produtos direto do celular. Ideal para o chão de estoque.</p>
                        </div>
                        <div className="space-y-3">
                            <button onClick={() => initMobileCollector()} className="w-full flex justify-center items-center gap-2 bg-indigo-600 text-white font-bold py-3 rounded-lg hover:bg-indigo-700 transition-colors">
                                <Smartphone size={18}/> Iniciar/Continuar Coleta Mobile
                            </button>
                            <button onClick={handleRefreshStatus} className="w-full flex justify-center items-center gap-2 text-indigo-600 font-bold py-2 hover:bg-indigo-50 rounded-lg transition-colors text-sm">
                                <RefreshCw size={16}/> Atualizar Status dos Aparelhos
                            </button>
                        </div>
                    </div>
                </div>

                {/* --- GERENCIAMENTO DE ÁREAS POR DEPÓSITO (ADMIN) --- */}
                {hasFullAccess && (
                    <div className="space-y-4">
                        {currentConfig.map(dep => (
                            <div key={dep.deposit_id} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                                <div className="flex justify-between items-center mb-4 border-b pb-2">
                                    <h3 className="text-lg font-bold text-gray-800">Depósito: {dep.deposit_name}</h3>
                                </div>
                                
                                {dep.areas.length === 0 ? (
                                    <p className="text-sm text-gray-400 italic">Depósito não incluído na contagem.</p>
                                ) : (
                                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                                        {dep.areas.map(a => {
                                            if (a.is_empty) {
                                                return (
                                                    <div key={a.area} className="p-3 border rounded-lg flex flex-col items-center bg-gray-100 border-gray-300 opacity-60">
                                                        <span className="text-[10px] font-bold text-gray-500 uppercase mb-1">ÁREA {a.area}</span>
                                                        <span className="text-sm font-black text-gray-600 mt-2 mb-2">VAZIA</span>
                                                        <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-gray-200 text-gray-600">Não Contar</span>
                                                    </div>
                                                );
                                            }

                                            const finishedInArea = activeAudit.sessions?.filter(s => s.current_deposit_id === dep.deposit_id && s.area_number === a.area && s.status === 'finished').length || 0;
                                            const totalInArea = activeAudit.sessions?.filter(s => s.current_deposit_id === dep.deposit_id && s.area_number === a.area).length || 0;
                                            const isAreaDone = a.is_done || finishedInArea >= a.expected;
                                            
                                            return (
                                                <div key={a.area} className={`p-3 border rounded-lg flex flex-col items-center ${isAreaDone ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'} group relative`}>
                                                    {/* Botão Marcar Vazio */}
                                                    {!isAreaDone && (
                                                        <button 
                                                            onClick={() => markAreaAsEmpty(dep.deposit_id, a.area)}
                                                            title="Marcar área como Vazia"
                                                            className="absolute top-1 right-1 p-1 bg-white hover:bg-gray-200 rounded text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                                        ><Trash2 size={12}/></button>
                                                    )}
                                                    
                                                    {/* Botão Forçar Conclusão (Check) */}
                                                    {!isAreaDone && (
                                                        <button 
                                                            onClick={() => markAreaAsDone(dep.deposit_id, a.area)}
                                                            title="Forçar Conclusão Manual desta Área"
                                                            className="absolute top-1 left-1 p-1 bg-white hover:bg-green-100 rounded text-green-600 opacity-0 group-hover:opacity-100 transition-opacity border border-green-200 shadow-sm"
                                                        ><Check size={12}/></button>
                                                    )}

                                                    <span className="text-[10px] font-bold text-gray-500 uppercase mb-1">ÁREA {a.area}</span>
                                                    
                                                    {!a.is_done ? (
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <button 
                                                                onClick={() => updateAreaExpected(dep.deposit_id, a.area, -1)} 
                                                                disabled={isApplying || a.expected <= totalInArea}
                                                                className="p-1 bg-white border rounded hover:bg-red-50 text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                                            ><Minus size={14}/></button>
                                                            <span className="text-lg font-black text-gray-800 w-6 text-center">{a.expected}</span>
                                                            <button 
                                                                onClick={() => updateAreaExpected(dep.deposit_id, a.area, 1)} 
                                                                disabled={isApplying}
                                                                className="p-1 bg-white border rounded hover:bg-green-50 text-green-600 disabled:opacity-30"
                                                            ><Plus size={14}/></button>
                                                        </div>
                                                    ) : (
                                                        <span className="text-sm font-black text-green-600 mt-2 mb-2">CONCLUÍDA</span>
                                                    )}

                                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${isAreaDone ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                                        {finishedInArea} / {a.expected}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ))}
                        
                        {/* NOVO: BOTÃO GLOBAL DE FORÇAR CONCLUSÃO */}
                        <div className="flex justify-end pt-4 pb-6">
                            <button 
                                onClick={forceGlobalConclusion} 
                                disabled={isApplying}
                                className="px-6 py-3 font-bold text-white bg-green-600 rounded-lg hover:bg-green-700 shadow-lg flex items-center gap-2 disabled:bg-green-400"
                            >
                                {isApplying ? <Loader2 size={18} className="animate-spin"/> : <FastForward size={18}/>}
                                GERAR DIVERGÊNCIAS (FORÇAR CONCLUSÃO)
                            </button>
                        </div>
                    </div>
                )}

                {/* PAINEL DE VISUALIZAÇÃO DOS APARELHOS CONECTADOS */}
                {activeAudit.sessions && activeAudit.sessions.length > 0 && (
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">Sessões Ativas por Aparelho</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            {activeAudit.sessions.map(s => (
                                <div key={s.id} onClick={() => handleViewSessionHistory(s)} className="p-4 border rounded-lg cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 transition-colors group">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="font-bold text-gray-700">{s.username}</span>
                                        <span className={`text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider ${s.status === 'active' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                                            {s.status === 'active' ? 'Em andamento' : 'Finalizado'}
                                        </span>
                                    </div>
                                    <div className="flex flex-col text-sm text-gray-500">
                                        <span className="truncate" title={s.deposit_name}>{s.deposit_name || 'Nenhum Depósito'}</span>
                                        <div className="flex justify-between items-center mt-1">
                                            <span>Área <strong className="text-indigo-600">{s.area_number || '-'}</strong> • Col. <strong className="text-indigo-600">{s.device_number}</strong></span>
                                            <Eye size={16} className="text-indigo-400 group-hover:text-indigo-600"/>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* --- MODAL DO HISTÓRICO COM PESQUISA E PAGINAÇÃO --- */}
                {viewingSession && (
                    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black bg-opacity-50 p-4 animate-in fade-in">
                        <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 h-[90vh] flex flex-col">
                            <div className="flex justify-between items-center mb-4 border-b pb-2">
                                <div>
                                    <h3 className="font-bold text-lg text-gray-800">Coletas: {viewingSession.username}</h3>
                                    <p className="text-xs text-gray-500">Área: {viewingSession.area_number || 1} | Coletor: {viewingSession.device_number} | Status: {viewingSession.status === 'active' ? 'Em andamento' : 'Finalizado'}</p>
                                </div>
                                <button onClick={() => setViewingSession(null)} className="text-gray-400 hover:text-red-500"><X size={20}/></button>
                            </div>
                            
                            {/* Barra de Pesquisa do Modal */}
                            <div className="mb-4 relative">
                                <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                <input 
                                    type="text" 
                                    placeholder="Buscar produto coletado..." 
                                    value={sessionSearchTerm}
                                    onChange={e => {
                                        setSessionSearchTerm(e.target.value);
                                        setSessionPage(1);
                                    }}
                                    className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                                />
                            </div>
                            
                            <div className="flex-1 overflow-y-auto pr-2 space-y-2 scrollbar-thin">
                                {isLoadingSession ? (
                                    <div className="flex justify-center p-8"><Loader2 className="animate-spin text-indigo-600" size={32} /></div>
                                ) : (() => {
                                    const filtered = viewingSessionHistory.filter(h => h.name.toLowerCase().includes(sessionSearchTerm.toLowerCase()));
                                    const totalPages = Math.ceil(filtered.length / 12) || 1;
                                    const displayed = filtered.slice((sessionPage - 1) * 12, sessionPage * 12);

                                    if (filtered.length === 0) return <p className="text-center text-gray-500 p-4">Nenhum item encontrado.</p>;

                                    return (
                                        <>
                                            <div className="space-y-2 mb-4">
                                                {displayed.map(h => (
                                                    <div key={h.id} className="flex justify-between items-center p-3 bg-gray-50 border border-gray-100 rounded-lg hover:bg-gray-100 transition-colors">
                                                        <div className="flex flex-col w-3/4">
                                                            <span className="font-bold text-sm text-gray-800 truncate" title={h.name}>{h.name}</span>
                                                            <span className="text-[10px] text-gray-500">{h.deposit_name} • Área {h.area_number} • {h.time}</span>
                                                        </div>
                                                        <div className="font-black text-indigo-600 bg-indigo-100 border border-indigo-200 px-3 py-1 rounded">
                                                            {h.qty}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Paginação */}
                                            {totalPages > 1 && (
                                                <div className="flex items-center justify-between border-t pt-4 mt-auto">
                                                    <button 
                                                        onClick={() => setSessionPage(p => Math.max(1, p - 1))}
                                                        disabled={sessionPage === 1}
                                                        className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded disabled:opacity-30 disabled:hover:bg-transparent"
                                                    ><ChevronLeft size={20}/></button>
                                                    
                                                    <span className="text-xs font-bold text-gray-500">Página {sessionPage} de {totalPages}</span>
                                                    
                                                    <button 
                                                        onClick={() => setSessionPage(p => Math.min(totalPages, p + 1))}
                                                        disabled={sessionPage === totalPages}
                                                        className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded disabled:opacity-30 disabled:hover:bg-transparent"
                                                    ><ChevronRight size={20}/></button>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                )}

                {success && <div className="p-4 bg-green-100 text-green-800 rounded-lg font-medium text-center">{success}</div>}
            </div>
        );
    };

    const renderMobile = () => {
        // Seletores baseados nas configurações daquele inventário ativo
        const config = activeAudit?.areas_config || [];
        // Mostra só os depósitos que tem ao menos 1 área ativa
        const availableDeposits = config.filter(c => c.areas.length > 0 && !c.areas.every(a => a.is_empty || a.is_done));
        
        // Pega as áreas do depósito que está selecionado no momento
        const currentDepositConfig = availableDeposits.find(c => c.deposit_id === parseInt(mobileDepositId));
        const availableAreas = currentDepositConfig ? currentDepositConfig.areas.filter(a => !a.is_empty && !a.is_done) : [];

        return (
            <div className="max-w-md mx-auto bg-white min-h-[80vh] flex flex-col shadow-xl">
                {/* OVERLAY DA CÂMERA */}
                {isScanning && (
                    <div className="fixed inset-0 z-[200] bg-black flex flex-col">
                        <div className="p-4 bg-gray-900 flex justify-between items-center text-white">
                            <h3 className="font-bold">Escaneando Código de Barras</h3>
                            <button onClick={() => setIsScanning(false)} className="p-2 bg-gray-800 hover:bg-gray-700 rounded-full transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="flex-1 flex flex-col justify-center items-center p-4">
                            <div id="reader" className="w-full max-w-sm rounded-lg overflow-hidden bg-black"></div>
                            <p className="text-gray-400 text-sm mt-4 text-center">Aponte a câmera para o código de barras (EAN)</p>
                        </div>
                    </div>
                )}

                <div className="bg-indigo-600 p-4 text-white flex justify-between items-center sticky top-0 z-20">
                    <div>
                        <h3 className="font-bold leading-none">Coleta Mobile</h3>
                        <p className="text-[10px] opacity-80">{activeAudit?.audit_number} - Col. {mobileDeviceNumber}</p>
                    </div>
                    <button onClick={() => setView(isOnlyCollector ? 'list' : 'manage')} className="p-1 bg-white/20 rounded hover:bg-white/30"><X size={18}/></button>
                </div>

                {/* --- SELETORES FIXOS NO TOPO DA TELA MOBILE --- */}
                <div className="bg-gray-50 border-b border-gray-200 p-4 space-y-3 z-10 sticky top-[60px]">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">1. Depósito</label>
                            <select 
                                value={mobileDepositId} 
                                onChange={e => {
                                    setMobileDepositId(e.target.value);
                                    setMobileAreaNumber(''); // Reseta a área ao trocar o depósito
                                }} 
                                className="w-full p-2 text-sm border border-indigo-200 rounded-lg bg-white outline-none font-bold text-indigo-900"
                            >
                                <option value="">Selecione...</option>
                                {availableDeposits.map(d => <option key={d.deposit_id} value={d.deposit_id}>{d.deposit_name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">2. Área Atual</label>
                            <select 
                                value={mobileAreaNumber} 
                                onChange={e => setMobileAreaNumber(e.target.value)} 
                                disabled={!mobileDepositId}
                                className="w-full p-2 text-sm border border-indigo-200 rounded-lg bg-white outline-none font-bold text-indigo-900 disabled:bg-gray-100 disabled:opacity-50"
                            >
                                <option value="">Selecione...</option>
                                {availableAreas.map(a => <option key={a.area} value={a.area}>Área {a.area}</option>)}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="flex-1 p-4 overflow-y-auto space-y-6">
                    {success && <div className="text-center p-2 bg-green-100 text-green-700 text-sm font-bold rounded animate-in fade-in">{success}</div>}
                    {error && <div className="text-center p-2 bg-red-100 text-red-700 text-sm font-bold rounded animate-in fade-in">{error}</div>}
                    
                    {/* BLOQUEIO DE TELA SE SESSÃO ESTIVER FINALIZADA */}
                    {mobileSessionStatus === 'finished' ? (
                        <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg text-center animate-in fade-in">
                            <CheckCircle className="mx-auto text-yellow-600 mb-2" size={32}/>
                            <h3 className="font-bold text-yellow-800 mb-1">Área Concluída</h3>
                            <p className="text-sm text-yellow-700">Você já finalizou a coleta para este Depósito/Área. Selecione outro destino no topo da tela para continuar bipando.</p>
                        </div>
                    ) : (
                        <>
                            {/* BUSCA PRODUTO */}
                            {!mobileSelectedProduct ? (
                                <div className="space-y-2 relative">
                                    <label className="font-bold text-sm text-gray-700">Buscar Produto (Código ou Nome)</label>
                                    <div className="relative">
                                        <input 
                                            type="text" placeholder={mobileDepositId && mobileAreaNumber ? "Bipar EAN ou digitar..." : "Selecione depósito e área acima"}
                                            disabled={!mobileDepositId || !mobileAreaNumber}
                                            value={mobileSearchQuery} onChange={e => setMobileSearchQuery(e.target.value)}
                                            className="w-full p-3 pl-10 pr-12 border-2 border-indigo-200 rounded-lg focus:border-indigo-600 outline-none text-lg disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        />
                                        <Search className="absolute left-3 top-3.5 text-gray-400" size={20}/>
                                        <button 
                                            type="button"
                                            disabled={!mobileDepositId || !mobileAreaNumber}
                                            onClick={() => setIsScanning(true)}
                                            className="absolute right-2 top-2 p-1.5 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 transition-colors disabled:opacity-50"
                                        >
                                            <Camera size={20}/>
                                        </button>
                                    </div>
                                    {mobileSearchResults.length > 0 && (
                                        <div className="absolute z-10 w-full bg-white border border-gray-200 shadow-xl rounded-lg max-h-60 overflow-y-auto">
                                            {mobileSearchResults.map(prod => (
                                                <div key={prod.id} onClick={() => selectMobileProduct(prod)} className="p-3 border-b border-gray-100 active:bg-indigo-100 cursor-pointer">
                                                    <p className="font-bold text-sm leading-tight text-gray-800">{prod.name}</p>
                                                    <p className="text-xs text-gray-500 mt-1">EAN: {prod.ean || '-'}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                /* FORMULÁRIO DE CONTAGEM */
                                <form onSubmit={handleMobileCollect} className="space-y-4 bg-indigo-50 p-4 rounded-xl border border-indigo-100 animate-in slide-in-from-right-4">
                                    <div className="flex justify-between items-start">
                                        <h4 className="font-black text-indigo-900 leading-tight">{mobileSelectedProduct.name}</h4>
                                        <button type="button" onClick={() => setMobileSelectedProduct(null)} className="text-indigo-400 p-1"><X size={16}/></button>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-2">
                                        <label className={`border-2 p-2 rounded-lg text-center font-bold text-sm cursor-pointer transition-colors ${mobileAction === 'sum' ? 'border-indigo-600 bg-indigo-100 text-indigo-800' : 'border-gray-200 bg-white text-gray-500'}`}>
                                            <input type="radio" name="action" className="hidden" checked={mobileAction === 'sum'} onChange={() => setMobileAction('sum')} />
                                            Somar (+)
                                        </label>
                                        <label className={`border-2 p-2 rounded-lg text-center font-bold text-sm cursor-pointer transition-colors ${mobileAction === 'replace' ? 'border-red-600 bg-red-100 text-red-800' : 'border-gray-200 bg-white text-gray-500'}`}>
                                            <input type="radio" name="action" className="hidden" checked={mobileAction === 'replace'} onChange={() => setMobileAction('replace')} />
                                            Substituir
                                        </label>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-indigo-900 mb-1">Quantidade Coletada</label>
                                        <input autoFocus type="number" step="0.01" required value={mobileQuantity} onChange={e => setMobileQuantity(e.target.value)}
                                            className="w-full p-4 border-2 border-indigo-200 rounded-lg text-2xl font-black text-center outline-none focus:border-indigo-600" />
                                    </div>

                                    <button type="submit" className="w-full bg-indigo-600 text-white font-black p-4 rounded-lg shadow-lg active:bg-indigo-800 transition-colors">
                                        SALVAR CONTAGEM
                                    </button>
                                </form>
                            )}
                        </>
                    )}

                    {/* HISTÓRICO DA SESSÃO ATUAL */}
                    {mobileHistory.length > 0 && (
                        <div className="mt-8 border-t pt-4">
                            <h4 className="font-bold text-gray-500 text-xs uppercase mb-3 flex items-center justify-between">
                                <span>Itens Coletados (Área {mobileAreaNumber})</span>
                                <span className="bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full">{mobileHistory.length}</span>
                            </h4>
                            <div className="space-y-2">
                                {mobileHistory.slice(0, 10).map(h => (
                                    <div key={h.id} className="flex justify-between items-center text-sm p-2 bg-gray-50 rounded border border-gray-100">
                                        <div className="flex flex-col truncate w-2/3">
                                            <span className="truncate font-medium text-gray-800">{h.name}</span>
                                            <span className="text-[10px] text-gray-400">{h.time}</span>
                                        </div>
                                        <span className="font-bold text-indigo-600 bg-indigo-100 px-2 py-1 rounded">
                                            {h.action === 'sum' ? '+' : '='}{h.qty}
                                        </span>
                                    </div>
                                ))}
                                {mobileHistory.length > 10 && (
                                    <p className="text-xs text-center text-gray-400 pt-2">Exibindo os 10 mais recentes.</p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-4 bg-gray-100 border-t sticky bottom-0">
                    <button 
                        onClick={finishMobileSession} 
                        disabled={!mobileDepositId || !mobileAreaNumber || mobileSessionStatus === 'finished'}
                        className="w-full bg-red-600 text-white font-bold p-4 rounded-lg shadow active:bg-red-800 text-sm disabled:opacity-50 disabled:bg-gray-400"
                    >
                        {mobileAreaNumber ? `FINALIZAR ÁREA ${mobileAreaNumber}` : 'FINALIZAR SESSÃO'}
                    </button>
                </div>
            </div>
        );
    }

    const renderDivergences = () => {
        // Cálculo dos Totais
        const isCompleted = activeAudit?.status === 'completed';
        const totalDiffCost = divergences.reduce((acc, curr) => acc + (curr.diff_total_cost || 0), 0);

        return (
            <div className="max-w-6xl mx-auto animate-in fade-in slide-in-from-bottom-4">
                <div className="flex justify-between items-end mb-6">
                    <div>
                        <button onClick={() => setView('list')} className="text-indigo-600 text-sm font-semibold hover:underline mb-2 flex items-center gap-1"><ArrowRight size={14} className="rotate-180"/> Voltar</button>
                        <h2 className="text-2xl font-black text-gray-800">Análise de Divergências</h2>
                        <p className="text-sm text-gray-600">Inventário: {activeAudit?.audit_number}</p>
                    </div>
                    <div className={`p-3 rounded-lg border-2 ${isCompleted ? 'bg-gray-100 border-gray-300' : (totalDiffCost < 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200')} text-right`}>
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Impacto Financeiro Previsto</p>
                        <p className={`text-xl font-black ${totalDiffCost < 0 ? 'text-red-600' : 'text-green-600'}`}>{formatCurrency(totalDiffCost)}</p>
                    </div>
                </div>

                {isCompleted && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg flex items-center gap-3">
                        <CheckCircle size={24}/>
                        <div>
                            <p className="font-bold">Inventário Efetivado</p>
                            <p className="text-sm">Os saldos do sistema já foram atualizados com base nesta contagem. Esta tela é apenas leitura.</p>
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm whitespace-nowrap">
                            <thead className="bg-gray-50 border-b text-gray-600 font-bold">
                                <tr>
                                    <th className="p-3">Produto</th>
                                    <th className="p-3">Depósito</th>
                                    <th className="p-3 text-center bg-gray-100">Saldo Sistema</th>
                                    <th className="p-3 text-center bg-blue-50">Coletado</th>
                                    <th className="p-3 text-center">Furo (Qtd)</th>
                                    <th className="p-3 text-right hidden md:table-cell">Custo Unitário</th>
                                    <th className="p-3 text-right">Diferença (R$)</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {divergences.map(item => (
                                    <tr key={item.id} className={item.diff_quantity !== 0 ? 'bg-orange-50/30' : ''}>
                                        <td className="p-3 font-medium text-gray-800 max-w-[200px] truncate" title={item.product_name}>{item.product_name}</td>
                                        <td className="p-3 text-gray-600">{item.deposit_name}</td>
                                        <td className="p-3 text-center font-bold bg-gray-50/50">{item.system_quantity}</td>
                                        
                                        {/* Célula Editável de Coleta */}
                                        <td className="p-3 text-center bg-blue-50/50">
                                            {isCompleted ? (
                                                <span className="font-bold text-blue-800">{item.collected_quantity}</span>
                                            ) : (
                                                <input 
                                                    type="number" step="0.01"
                                                    value={item.collected_quantity}
                                                    onChange={(e) => updateDivergenceItem(item.id, e.target.value)}
                                                    className="w-20 text-center p-1 border border-blue-300 rounded bg-white text-blue-900 font-bold outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            )}
                                        </td>

                                        <td className="p-3 text-center font-bold">
                                            <span className={item.diff_quantity > 0 ? 'text-green-600' : item.diff_quantity < 0 ? 'text-red-600' : 'text-gray-400'}>
                                                {item.diff_quantity > 0 ? '+' : ''}{item.diff_quantity}
                                            </span>
                                        </td>
                                        <td className="p-3 text-right text-gray-500 hidden md:table-cell">{formatCurrency(item.cost_price)}</td>
                                        <td className="p-3 text-right font-bold">
                                            <span className={item.diff_total_cost > 0 ? 'text-green-600' : item.diff_total_cost < 0 ? 'text-red-600' : 'text-gray-400'}>
                                                {formatCurrency(item.diff_total_cost)}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {!isCompleted && (
                    <div className="flex flex-wrap justify-end gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <button onClick={handleReopenCollect} disabled={isApplying} className="px-4 py-2 font-bold text-orange-600 bg-white border border-orange-300 rounded-lg hover:bg-orange-50 flex items-center gap-2">
                            <Unlock size={18}/> Reabrir Coleta
                        </button>
                        <button onClick={exportDivergencesToCSV} className="px-4 py-2 font-bold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 flex items-center gap-2">
                            <Download size={18}/> Exportar Divergências
                        </button>
                        <button 
                            onClick={applyInventory} 
                            disabled={isApplying}
                            className="px-6 py-2 font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 shadow flex items-center gap-2 disabled:bg-indigo-400"
                        >
                            {isApplying ? <Loader2 size={18} className="animate-spin"/> : <Save size={18}/>}
                            EFETIVAR E ATUALIZAR SISTEMA
                        </button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="p-4 md:p-8 w-full max-w-full pb-24 h-full relative">
            {view === 'list' && renderList()}
            {view === 'create' && renderCreate()}
            {view === 'manage' && renderManage()}
            
            {/* O mobile View cobre a tela inteira com position absolute para focar só na coleta */}
            {view === 'mobile' && (
                <div className="fixed inset-0 z-[100] bg-gray-100 overflow-y-auto">
                    {renderMobile()}
                </div>
            )}

            {view === 'divergence' && renderDivergences()}
        </div>
    );
};

export default InventoryAuditPage;